import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Camera, Download, Eye, FlaskConical, History, Share2, Users } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { canEdit, watchProject } from '../services/projects';
import { watchFormulations } from '../services/formulations';
import { watchCaptures } from '../services/captures';
import type { Capture, Formulation, Project } from '../lib/types';
import { Badge, Button, EmptyState, Modal, Spinner } from './ui';
import FormulationTable from './FormulationTable';
import FormulationDialog from './FormulationDialog';
import CaptureBar from './CaptureBar';
import CaptureHistory from './CaptureHistory';
import ConfirmationScreen from './ConfirmationScreen';
import ReaderManager from './ReaderManager';
import { watchReadings } from '../services/readings';
import type { Reading } from '../services/readings';
import type { UsedReader } from './ReaderManager';
import { ensureBuiltInReaders, watchReaders } from '../services/readers';
import type { Reader } from '../lib/types';
import ShareDialog from './ShareDialog';
import NotebookSection from './NotebookSection';
import ReadingsView from './ReadingsView';
import ReaderCapture from './ReaderCapture';
import type { ReaderSession } from './ReaderCapture';
import { ProjectReadersDialog, ReaderPicker } from './ProjectReaders';
import type { LearnedReader, ReaderExtraction } from '../services/readings';
import { rejectCapture } from '../services/captures';
import type { ProjectReader } from '../lib/types';
import type { ConfirmationSource } from './ConfirmationScreen';

export default function ProjectView() {
  const { projectId = '' } = useParams();
  const { user } = useAuth();
  const uid = user?.uid;

  const [project, setProject] = useState<Project | null | 'missing'>(null);
  const [formulations, setFormulations] = useState<Formulation[] | null>(null);
  const [captures, setCaptures] = useState<Capture[]>([]);
  const [editing, setEditing] = useState<Formulation | 'new' | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState<ConfirmationSource | null>(null);
  const [exporting, setExporting] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [readersOpen, setReadersOpen] = useState(false);
  const [readers, setReaders] = useState<Reader[]>([]);
  const [readings, setReadings] = useState<Reading[]>([]);
  const [exportOpen, setExportOpen] = useState(false);
  const [readerSession, setReaderSession] = useState<ReaderSession | null>(null);
  const [discarding, setDiscarding] = useState<string | null>(null);

  /**
   * Readers other people have captured with here, rebuilt from the snapshot
   * each capture carries. This is the only way they are visible — their
   * definitions live in someone else's account and are not readable from here.
   */
  const usedReaders = useMemo(() => {
    const seen = new Map<string, UsedReader>();
    for (const c of captures) {
      const snap = (c as unknown as { reader?: UsedReader }).reader;
      if (snap?.id && !seen.has(snap.id)) {
        seen.set(snap.id, { ...snap, byName: c.capturedByName ?? null });
      }
    }
    return [...seen.values()];
  }, [captures]);

  useEffect(() => {
    if (!projectId) return;
    // Losing access mid-session makes the document unreadable rather than
    // absent. Both end up here, and both mean the same thing to the user.
    return watchProject(
      projectId,
      (p) => setProject(p ?? 'missing'),
      () => setProject('missing'),
    );
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    return watchFormulations(projectId, setFormulations);
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    return watchCaptures(projectId, setCaptures);
  }, [projectId]);

  useEffect(() => watchReadings(projectId, setReadings), [projectId]);

  /** Readers that actually have readings here, for the per-reader export. */
  const readerOptions = useMemo(() => {
    const seen = new Map<string, { id: string; name: string; count: number }>();
    for (const r of readings) {
      const prev = seen.get(r.readerId);
      if (prev) prev.count++;
      else seen.set(r.readerId, { id: r.readerId, name: r.readerName || 'Readings', count: 1 });
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [readings]);

  /**
   * A project created since readers became a per-project choice carries a
   * `readers` list, even an empty one. Older projects have none and keep the
   * fixed formulation table with the capturer's own readers, exactly as before.
   */
  const readerMode = project !== null && project !== 'missing' && Array.isArray(project.readers);
  const legacyProject = project !== null && project !== 'missing' && !readerMode;

  // The person's own readers: what an older project captures with, and what a
  // newer one can pick from.
  useEffect(() => {
    if (!uid) return undefined;
    return watchReaders(uid, setReaders);
  }, [uid]);

  // Older projects were built around the app's readers being present in every
  // account; seeding stays for them only. A no-op once the collection exists.
  useEffect(() => {
    if (uid && legacyProject) void ensureBuiltInReaders(uid).catch(() => {});
  }, [uid, legacyProject]);

  /**
   * Trial photos taken to set up a reader, read but not yet saved. They are
   * usually the first real result, so they wait here until the person decides.
   */
  const trials = useMemo(
    () =>
      captures
        .map((c) => c as Capture & { trial?: boolean; readerExtraction?: ReaderExtraction })
        .filter((c) => c.trial && c.status === 'extracted' && c.readerExtraction),
    [captures],
  );

  const reviewLearned = useCallback((captureId: string, learned: LearnedReader) => {
    setReaderSession({
      kind: 'stored',
      reader: { ...learned.reader },
      captureId,
      extraction: learned.extraction,
    });
  }, []);

  const openConfirmation = useCallback((source: ConfirmationSource) => {
    setPendingConfirmation(source);
  }, []);

  /**
   * A capture that finished extracting while the user was elsewhere still needs
   * a human. Surface the count rather than auto-applying anything.
   */
  const awaitingConfirmation = useMemo(
    () => captures.filter((c) => c.status === 'extracted').length,
    [captures],
  );

  if (project === 'missing') {
    return (
      <EmptyState
        title="Project not available"
        body="It may have been deleted, or your access to it removed."
        action={
          <Link to="/">
            <Button variant="secondary" icon={<ArrowLeft size={16} />}>
              Back to projects
            </Button>
          </Link>
        }
      />
    );
  }

  if (!uid || project === null || formulations === null) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const isOwner = project.ownerUid === uid;
  const readOnly = !canEdit(project, uid);
  const memberCount = Object.keys(project.members ?? {}).length;
  const projectReaders = project.readers ?? [];
  const showCaptureBar = !readOnly && (!readerMode || projectReaders.length > 0);

  return (
    <div
      // Clears the fixed capture bar plus the phone's own system bar, so the
      // last formulation is never hidden behind either.
      style={
        showCaptureBar
          ? { paddingBottom: 'calc(11rem + env(safe-area-inset-bottom, 0px))' }
          : undefined
      }
    >
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Link
            to="/"
            className="mb-1 inline-flex items-center gap-1 text-sm text-ink-400 hover:text-ink-100 pointer-coarse:min-h-11"
          >
            <ArrowLeft size={14} />
            Projects
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="truncate text-xl font-semibold">{project.name}</h1>
            {!isOwner && (
              <Badge tone="neutral">
                <Eye size={10} />
                shared with you{readOnly ? ' · view only' : ''}
              </Badge>
            )}
            {memberCount > 1 && (
              <Badge tone="info">
                <Users size={10} />
                {memberCount}
              </Badge>
            )}
          </div>
          {project.description && (
            <p className="mt-0.5 text-sm text-ink-400">{project.description}</p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            icon={<History size={16} />}
            onClick={() => setShowHistory(true)}
            className={awaitingConfirmation > 0 ? 'text-amber-300' : undefined}
          >
            History
            {awaitingConfirmation > 0 && (
              <span className="ml-1 rounded-full bg-amber-500 px-1.5 text-xs font-semibold text-ink-950">
                {awaitingConfirmation}
              </span>
            )}
          </Button>
          <Button
            variant="secondary"
            icon={<Download size={16} />}
            disabled={formulations.length === 0 && readings.length === 0}
            loading={exporting}
            onClick={async () => {
              // SheetJS is a third of the bundle. Nobody on a lab phone should
              // download it until they actually press Export.
              setExporting(true);
              try {
                const { exportProject } = await import('../services/exportXlsx');
                exportProject(project, formulations, captures, readings);
              } finally {
                setExporting(false);
              }
            }}
          >
            Export .xlsx
          </Button>

          {/* "Send me the zeta data" is a routine request. It should not mean
              sending the whole workbook and asking someone to find the tab. */}
          {readerOptions.length > 0 && (
            <div className="relative">
              <Button
                variant="ghost"
                icon={<Download size={16} />}
                onClick={() => setExportOpen((o) => !o)}
              >
                One reader
              </Button>
              {exportOpen && (
                <div className="absolute right-0 z-20 mt-1 min-w-52 rounded-lg border border-ink-700 bg-ink-900 p-1 shadow-xl">
                  {readerOptions.map((r) => (
                    <button
                      key={r.id}
                      onClick={async () => {
                        setExportOpen(false);
                        const { exportReaderReadings } = await import('../services/exportXlsx');
                        exportReaderReadings(project, captures, readings, r.id);
                      }}
                      className="flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm text-ink-200 hover:bg-ink-800"
                    >
                      <span className="truncate">{r.name}</span>
                      <span className="shrink-0 tabular-nums text-xs text-ink-500">{r.count}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <Button
            variant="ghost"
            icon={<FlaskConical size={16} />}
            onClick={() => setReadersOpen(true)}
          >
            Readers
          </Button>
          {isOwner && (
            <Button variant="ghost" icon={<Share2 size={16} />} onClick={() => setSharing(true)}>
              Share
            </Button>
          )}

        </div>
      </div>

      {!readOnly && trials.length > 0 && (
        <div className="mb-4 space-y-2">
          {trials.map((c) => {
            const extraction = c.readerExtraction!;
            const samples = new Set(extraction.readings.map((r) => r.sampleLabel)).size;
            const reader: ProjectReader = projectReaders.find((r) => r.id === extraction.readerId) ??
              readers.find((r) => r.id === extraction.readerId) ?? {
              id: extraction.readerId,
              name: extraction.reader.name,
              description: '',
              fields: extraction.reader.fields.map((f) => ({
                key: f.key,
                label: f.label,
                unit: f.unit ?? '',
                decimals: 2,
                min: null,
                max: null,
                aliases: [],
              })),
            };
            return (
              <div
                key={c.id}
                className="flex flex-wrap items-center gap-3 rounded-xl border border-sky-800 bg-sky-950/60 p-3"
              >
                <p className="min-w-0 flex-1 text-sm text-sky-100">
                  Trial photo from setting up <strong>{reader.name}</strong>:{' '}
                  {extraction.readings.length} reading{extraction.readings.length === 1 ? '' : 's'}{' '}
                  from {samples} sample{samples === 1 ? '' : 's'}, not saved yet.
                </p>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setDiscarding(c.id)}>
                    Discard
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() =>
                      setReaderSession({ kind: 'stored', reader, captureId: c.id, extraction })
                    }
                  >
                    Review and save
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={discarding !== null} onClose={() => setDiscarding(null)} title="Discard trial data?">
        <p className="text-sm text-ink-300">
          The readings in this trial photo will not be saved. The reader it set up stays.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setDiscarding(null)}>
            Keep it
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              if (discarding) await rejectCapture(projectId, discarding).catch(() => {});
              setDiscarding(null);
            }}
          >
            Discard
          </Button>
        </div>
      </Modal>

      <NotebookSection project={project} readings={readings} readOnly={readOnly} />

      {readerMode ? (
        projectReaders.length === 0 ? (
          isOwner ? (
            <section className="mx-auto max-w-2xl rounded-xl border border-ink-700 bg-ink-900/40 p-5">
              <h2 className="text-base font-semibold">What will this project record?</h2>
              <p className="mb-5 mt-1 text-sm text-ink-400">
                Choose a reader to start — one that comes with the app, one of yours, or a new one.
                You can add more at any time.
              </p>
              <ReaderPicker
                projectId={projectId}
                chosen={projectReaders}
                personal={readers}
                onReview={reviewLearned}
              />
            </section>
          ) : (
            <EmptyState
              title="Nothing to record into yet"
              body="The project owner has not chosen what this project records. Once they do, you can start capturing here."
            />
          )
        ) : (
          <ReadingsView
            projectId={projectId}
            readers={projectReaders}
            readings={readings}
            canDelete={isOwner}
          />
        )
      ) : formulations.length === 0 ? (
        <EmptyState
          title="Nothing captured yet"
          icon={readOnly ? undefined : <Camera size={28} />}
          body={
            readOnly
              ? 'Nothing has been added to this project yet.'
              : 'Photograph an instrument screen and BlisxBench reads the sample name along with the values, creating the sample for you. Voice and manual entry work the same way.'
          }
        />
      ) : (
        <FormulationTable
          projectId={projectId}
          formulations={formulations}
          readOnly={readOnly}
          canManage={isOwner}
          onEdit={setEditing}
          onCapture={openConfirmation}
        />
      )}

      {showCaptureBar && (
        <CaptureBar
          uid={uid}
          projectId={projectId}
          formulations={formulations}
          readers={readerMode ? projectReaders : readers}
          onCaptured={openConfirmation}
          onReaderSession={readerMode ? setReaderSession : undefined}
        />
      )}

      {readerSession && (
        <ReaderCapture
          projectId={projectId}
          session={readerSession}
          onClose={() => setReaderSession(null)}
        />
      )}

      <FormulationDialog
        projectId={projectId}
        target={editing}
        onClose={() => setEditing(null)}
      />

      <CaptureHistory
        open={showHistory}
        onClose={() => setShowHistory(false)}
        captures={captures}
        formulations={formulations}
        readOnly={readOnly}
        onReopen={openConfirmation}
      />

      {readerMode ? (
        <ProjectReadersDialog
          open={readersOpen}
          onClose={() => setReadersOpen(false)}
          projectId={projectId}
          isOwner={isOwner}
          chosen={projectReaders}
          personal={readers}
          onReview={reviewLearned}
        />
      ) : (
        <ReaderManager
          open={readersOpen}
          onClose={() => setReadersOpen(false)}
          uid={uid}
          readers={readers}
          used={usedReaders}
          projectId={projectId}
          onReview={reviewLearned}
        />
      )}

      {sharing && (
        <ShareDialog open onClose={() => setSharing(false)} project={project} currentUid={uid} />
      )}

      {pendingConfirmation && (
        <ConfirmationScreen
          projectId={projectId}
          formulations={formulations}
          source={pendingConfirmation}
          onReExtract={setPendingConfirmation}
          onClose={() => setPendingConfirmation(null)}
        />
      )}
    </div>
  );
}
